// Shared GST snapshot card (Top Level + Finance dashboards), shown alongside the Receivables /
// Payables SummaryCards and styled to match them. Fed by GetGSTPositionSummary
// (see spGetGSTPositionSummary.sql):
//   * covers the previous calendar month (FromDate)
//   * Outward = GST on all non-rejected client invoices + client debit notes - client credit notes
//   * Inward  = creditable GST on all non-rejected vendor invoices (supplier + SPPO) + vendor credit
//               notes - vendor debit notes (non-creditable GST and its notes excluded)
//   * GST paid = GST payments (bank transactions) made in the current month, i.e. against the
//     previous month's liability
import React from 'react';
import { Loader2, Receipt } from 'lucide-react';
import { formatCurrency } from '../../utilities/formatCurrency';

// Same tone set as SummaryCard — orange while GST is payable, green when in credit.
const GST_TONES = {
    orange: { card: 'bg-orange-50 dark:bg-orange-900/20', icon: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-500' },
    green:  { card: 'bg-green-50 dark:bg-green-900/20',   icon: 'bg-green-100 dark:bg-green-900/30',   text: 'text-green-600 dark:text-green-500' },
};

const monthLabel = (date) => date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

const Row = ({ label, value, dot, bold, sub }) => (
    <li className={`flex items-center justify-between ${sub ? 'py-0.5 pl-4 text-[11px]' : 'py-1'}`}>
        <span className={`flex items-center ${sub ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'} ${bold ? 'font-semibold' : ''}`}>
            {dot && <span className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${dot}`} />}
            {label}
        </span>
        <span className={`tabular-nums whitespace-nowrap ml-2 ${bold ? 'font-bold' : 'font-medium'} text-gray-900 dark:text-white`}>
            ₹{formatCurrency(value || 0)}
        </span>
    </li>
);

const SplitLine = ({ igst, cgst, sgst }) => (
    <li className="flex items-center justify-between py-0.5 pl-4 text-[10px] text-gray-400 dark:text-gray-500">
        <span>IGST · CGST · SGST</span>
        <span className="tabular-nums whitespace-nowrap ml-2 text-gray-600 dark:text-gray-300">
            {formatCurrency(igst || 0)} · {formatCurrency(cgst || 0)} · {formatCurrency(sgst || 0)}
        </span>
    </li>
);

const Divider = () => <li className="my-1 border-t border-dashed border-gray-200 dark:border-gray-700" />;

const GSTPositionCard = ({ loading, summary }) => {
    const outward = summary?.ClientGSTTotal || 0;
    const inward = summary?.VendorGSTTotal || 0;
    const net = outward - inward;
    const isCredit = net < 0;
    const t = isCredit ? GST_TONES.green : GST_TONES.orange;

    const fromDate = summary?.FromDate ? new Date(summary.FromDate) : null;
    const gstMonth = fromDate ? monthLabel(fromDate) : '';
    const paymentMonth = fromDate ? monthLabel(new Date(fromDate.getFullYear(), fromDate.getMonth() + 1, 1)) : '';
    const gstPaid = summary?.GSTPaid || 0;
    const paidOn = summary?.GSTPaidDate
        ? new Date(summary.GSTPaidDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
        : '';

    // Outward vs inward proportion bar, mirroring the age-bucket bar on Receivables / Payables.
    const barTotal = outward + inward;

    return (
        <div className="h-full bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors">
            <div className={`flex items-center justify-between px-3 py-2 ${t.card}`}>
                <div className="flex items-center space-x-2 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${t.icon}`}>
                        <Receipt className={`w-4 h-4 ${t.text}`} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">GST position</p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight truncate">
                            {loading || !summary ? 'Net GST for last month' : `${isCredit ? 'Net credit' : 'Net payable'}${gstMonth ? ` · ${gstMonth}` : ''}`}
                        </p>
                    </div>
                </div>
                {loading ? (
                    <Loader2 className={`w-4 h-4 animate-spin ${t.text}`} />
                ) : (
                    <span className="text-base font-bold text-gray-900 dark:text-white whitespace-nowrap ml-2">
                        ₹{formatCurrency(Math.abs(net))}
                    </span>
                )}
            </div>

            <div className="px-3 py-2">
                {!loading && summary && (
                    <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 mb-2">
                        {barTotal > 0 ? (
                            <>
                                <div className="bg-orange-500 h-full" style={{ width: `${(outward / barTotal) * 100}%` }} />
                                <div className="bg-blue-400 h-full" style={{ width: `${(inward / barTotal) * 100}%` }} />
                            </>
                        ) : (
                            <div className="h-full w-full bg-gray-200 dark:bg-gray-600" />
                        )}
                    </div>
                )}
                {loading || !summary ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500 py-3 text-center">
                        {loading ? 'Loading…' : 'No data available'}
                    </p>
                ) : (
                    <ul className="text-xs">
                        <Row label="Outward GST (clients)" value={outward} dot="bg-orange-500" bold />
                        <Row label="Invoices" value={summary?.ClientInvoiceGST} sub />
                        <Row label="+ Debit notes" value={summary?.ClientDebitNoteGST} sub />
                        <Row label="− Credit notes" value={summary?.ClientCreditNoteGST} sub />
                        <SplitLine igst={summary?.ClientIGST} cgst={summary?.ClientCGST} sgst={summary?.ClientSGST} />
                        <Divider />
                        <Row label="Inward GST (vendors)" value={inward} dot="bg-blue-400" bold />
                        <Row label="Invoices" value={summary?.VendorInvoiceGST} sub />
                        <Row label="+ Credit notes" value={summary?.VendorCreditNoteGST} sub />
                        <Row label="− Debit notes" value={summary?.VendorDebitNoteGST} sub />
                        <SplitLine igst={summary?.VendorIGST} cgst={summary?.VendorCGST} sgst={summary?.VendorSGST} />
                        <Divider />
                        <li className="flex items-center justify-between py-1">
                            <span className="flex items-center text-gray-600 dark:text-gray-300">
                                <span className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${gstPaid > 0 ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                                GST paid{paymentMonth ? ` in ${paymentMonth}` : ''}
                            </span>
                            <span className={`tabular-nums whitespace-nowrap ml-2 font-medium ${gstPaid > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                {gstPaid > 0 ? `₹${formatCurrency(gstPaid)}` : 'Not paid yet'}
                            </span>
                        </li>
                    </ul>
                )}
                {gstPaid > 0 && paidOn && !loading && (
                    <p className="mt-1 text-[10px] text-gray-400 dark:text-gray-500">Last payment on {paidOn}</p>
                )}
            </div>
        </div>
    );
};

export default GSTPositionCard;
